import React from "react";
import { Mic, MicOff } from "lucide-react";
import { useMicLevel } from "@/hooks/useMicLevel";
import { useI18n } from "@/i18n/I18nContext";

interface MicVisualizerProps {
  testId?: string;
  variant?: "test" | "voice";
}

// Experimental input visualizer — shows raw microphone LEVEL only.
// This is NOT pitch detection (that lands in Build 2).
export const MicVisualizer: React.FC<MicVisualizerProps> = ({
  testId = "reflection-mic-record-btn",
  variant = "test",
}) => {
  const { active, level, error, start, stop } = useMicLevel();
  const { t } = useI18n();
  const bars = 14;
  const label = active ? t("mic.listening") : variant === "voice" ? t("mic.voiceNote") : t("mic.test");

  return (
    <div className="flex items-center gap-3">
      <button
        data-testid={testId}
        onClick={() => (active ? stop() : start())}
        className="flex items-center gap-2 rounded-full px-4 py-2 text-xs font-mono tracking-wide transition active:scale-95"
        style={{
          background: active ? "rgba(56,189,248,0.12)" : "rgba(255,255,255,0.04)",
          border: `1px solid ${active ? "rgba(56,189,248,0.35)" : "rgba(255,255,255,0.1)"}`,
          color: active ? "#bae6fd" : "#cbd5e1",
        }}
      >
        {active ? <Mic size={14} /> : <MicOff size={14} />}
        {label}
      </button>

      <div className="flex h-6 items-end gap-[3px]">
        {Array.from({ length: bars }).map((_, i) => {
          const threshold = (i + 1) / bars;
          const on = active && level >= threshold * 0.85;
          return (
            <div
              key={i}
              className="w-[3px] rounded-full transition-all duration-75"
              style={{
                height: on ? `${8 + threshold * 16}px` : "4px",
                background: on ? "#38BDF8" : "rgba(148,163,184,0.2)",
              }}
            />
          );
        })}
      </div>

      <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">
        {t("common.experimental")}
      </span>
      {error && <span className="text-[10px] text-slate-500">{t("mic.error")}</span>}
    </div>
  );
};
